import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { GraphMeeting } from "@/api/home-context"

import { useTypewriter } from "@/hooks/use-typewriter"

/** Kept in sync with `useTypewriter` `duration` below for ChatHome example-stagger timing. */
export const GREETING_TYPEWRITER_DURATION_MS = 800

type GreetingPersonalization = {
  // First upcoming meeting starting soon (within 30 min) — same shape as home_context
  nextMeeting?: GraphMeeting | null
  // Title of the most recent in-flight chat the user could resume
  continueChatTitle?: string | null
  // Theme label from the activity digest (this week's focus)
  themeLabel?: string | null
  // Most frequent collaborator (top of /me/people, name only)
  topCollaboratorName?: string | null
}

type GreetingProps = {
  userName?: string
  typewriterStartDelayMs?: number
  // Compatibility prop — used only for the muted theme subtitle when no
  // richer personalization is supplied. Prefer passing `personalization`.
  themeLabel?: string
  personalization?: GreetingPersonalization
}

function timeSlot(hour: number): "night" | "morning" | "afternoon" | "evening" {
  if (hour < 5) return "night"
  if (hour < 12) return "morning"
  if (hour < 18) return "afternoon"
  return "evening"
}

function minutesUntilStart(startIso: string | null | undefined): number | null {
  if (!startIso) return null
  const start = new Date(startIso).getTime()
  if (Number.isNaN(start)) return null
  const diff = Math.round((start - Date.now()) / 60000)
  return diff
}

function stableIndex(seed: string, length: number): number {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  }
  return hash % length
}

function daySeed(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, "0")
  const day = String(now.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

// `t(key, { returnObjects: true })` returns the raw array without applying
// {{placeholder}} substitution, so we interpolate manually on the picked entry.
function interpolatePlaceholders(
  template: string,
  values: Record<string, string | number | null | undefined>
): string {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key: string) => {
    const value = values[key]
    return value === null || value === undefined ? "" : String(value)
  })
}

export function Greeting({
  userName,
  typewriterStartDelayMs = 0,
  themeLabel,
  personalization,
}: GreetingProps) {
  const { i18n, t } = useTranslation()

  // Resolve the most personal greeting we can. Order of precedence:
  // 1. Pre-meeting hint when a calendar event is imminent
  // 2. Resume hint when there's an in-flight chat to continue
  // 3. Theme-aware time-of-day variant ("Good morning Daniel — still on Q2 forecast?")
  // 4. Plain time-of-day variant
  // 5. Random pool fallback (legacy behavior)
  const greeting = useMemo(() => {
    const firstName = userName ? userName.split(" ")[0] : null
    const namePart = firstName ?? ""
    const hour = new Date().getHours()
    const slot = timeSlot(hour)
    const p = personalization
    const pickGreeting = (
      key: string,
      values: Record<string, string | number | null | undefined>,
      fallback: string
    ): string => {
      const translated = t(key, { ...values, returnObjects: true, defaultValue: fallback }) as
        | string
        | string[]
      if (Array.isArray(translated)) {
        if (translated.length === 0) return fallback
        const seed = [
          i18n.resolvedLanguage,
          key,
          daySeed(),
          ...Object.values(values).map((value) => String(value ?? "")),
        ].join(":")
        return interpolatePlaceholders(translated[stableIndex(seed, translated.length)], values)
      }
      return translated || fallback
    }

    // 1. Imminent meeting (next 30 min)
    if (p?.nextMeeting?.subject) {
      const minutes = minutesUntilStart(p.nextMeeting.start_iso)
      if (minutes !== null && minutes >= 0 && minutes <= 30) {
        const preMeetingKey = firstName
          ? "greeting.contextual.preMeeting.personalized"
          : "greeting.contextual.preMeeting.generic"
        return pickGreeting(
          preMeetingKey,
          {
            name: namePart,
            subject: p.nextMeeting.subject,
            minutes,
          },
          namePart
            ? `${namePart}, ${p.nextMeeting.subject} starts in ${minutes} min.`
            : `${p.nextMeeting.subject} starts in ${minutes} min.`
        )
      }
    }

    // 2. Resume hint
    const resumeTitle = p?.continueChatTitle?.trim()
    if (resumeTitle) {
      const resumeKey = firstName
        ? "greeting.contextual.resume.personalized"
        : "greeting.contextual.resume.generic"
      return pickGreeting(
        resumeKey,
        {
          name: namePart,
          title: resumeTitle,
        },
        namePart
          ? `Welcome back ${namePart} — pick up "${resumeTitle}"?`
          : `Welcome back — pick up "${resumeTitle}"?`
      )
    }

    // 3. Theme-aware time-of-day variant
    const focusTheme = p?.themeLabel?.trim()
    if (focusTheme) {
      const themedSlotKey = firstName
        ? `greeting.contextual.${slot}WithTheme.personalized`
        : `greeting.contextual.${slot}WithTheme.generic`
      const themedValue = pickGreeting(
        themedSlotKey,
        {
          name: namePart,
          theme: focusTheme,
        },
        ""
      )
      if (themedValue && themedValue !== themedSlotKey) {
        return themedValue
      }
    }

    // 4. Plain time-of-day variant
    const slotKey = firstName
      ? `greeting.timed.${slot}.personalized`
      : `greeting.timed.${slot}.generic`
    const slotValue = pickGreeting(slotKey, { name: namePart }, "")
    if (typeof slotValue === "string" && slotValue && slotValue !== slotKey) {
      return firstName ? slotValue.replace("{{name}}", firstName) : slotValue
    }

    // 5. Random pool fallback
    const greetingType = firstName ? "personalized" : "generic"
    const greetings = t(`greeting.${greetingType}`, { returnObjects: true }) as string[]
    if (!Array.isArray(greetings) || greetings.length === 0) {
      return ""
    }
    const randomIndex = Math.floor(Math.random() * greetings.length)
    let selectedGreeting = greetings[randomIndex]
    if (firstName) {
      selectedGreeting = selectedGreeting.replace("{{name}}", firstName)
    }
    return selectedGreeting
  }, [i18n.resolvedLanguage, t, userName, personalization])

  // Animate on every mount: the user lands on ChatHome each time they open a
  // module and expects the greeting to type in, not appear fully formed.
  const { display: displayedGreeting } = useTypewriter({
    text: greeting,
    duration: GREETING_TYPEWRITER_DURATION_MS,
    startDelayMs: typewriterStartDelayMs,
  })

  if (!greeting) {
    return null
  }

  // Show a muted subtitle for the theme only when the greeting itself doesn't
  // already weave it in (i.e. when we fell through to a non-themed variant).
  const subtitleTheme =
    themeLabel?.trim() && !greeting.includes(themeLabel.trim()) ? themeLabel.trim() : null

  return (
    <div data-testid="greeting-wrapper">
      <h2
        className="text-foreground/90 min-h-[2rem] text-center text-2xl font-medium"
        data-testid="greeting"
      >
        {displayedGreeting}
      </h2>
      {subtitleTheme ? (
        <p
          className="text-muted-foreground mt-2 text-center text-sm"
          data-testid="greeting-theme-label"
        >
          {t("chatHome.themeLabel", {
            theme: subtitleTheme,
            defaultValue: `Focused on ${subtitleTheme} this week.`,
          })}
        </p>
      ) : null}
    </div>
  )
}
